// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { createSelector } from '@reduxjs/toolkit';
import type { RootState } from 'Store';
import { orderStatusCode } from 'Utils/constants';
import { resolveProjectStatus } from 'Utils/projectStatus';
import { resolveEffectiveOrderSortFilter } from './resolveEffectiveOrderSortFilter';
import { hasLocation } from './filterProjects';

const selectOrderSortFilter = (state: RootState) => state.projectList.orderSortFilter;
const selectOrderSortFilterTouched = (state: RootState) => state.projectList.orderSortFilterTouched;
const selectKeywordFilter = (state: RootState) => state.projectList.keywordFilter;

// Used both to build the projects request and to decide what the filter dropdown displays, so
// the two never disagree. The rule itself lives in resolveEffectiveOrderSortFilter, shared with
// the reducer that applies it without a store to select from.
// Selecting the three individual fields (rather than the whole projectList slice) keeps this
// from recomputing on unrelated slice updates like the loaded project list.
const selectMapFolderId = (state: RootState) => state.projectList.mapFolderId;

const selectEffectiveOrderSortFilter = createSelector(
  [selectOrderSortFilter, selectOrderSortFilterTouched, selectKeywordFilter, selectMapFolderId],
  resolveEffectiveOrderSortFilter,
);

// Everything a project card shows about its orders, derived from the project it is handed.
//
// Two shapes arrive here. The folder page carries a project's raw orders; the dashboard renders
// the complete set, whose entries carry the representative order's fields flattened onto the
// project because the server already made that choice. Both are read below.
//
// The project is read as passed rather than looked back up in a store list: every caller maps
// straight over the array it renders, so the lookup only ever returned the same object - and
// once two shapes are in play, re-finding one in a list of the other silently swaps which shape
// a card is described from.
// Which of a project's orders speaks for it: the newest submitted one, falling back to the
// newest draft when nothing was ever submitted - the pick the server makes when it builds the
// complete set (see selectProjectInfo).
const pickRepresentativeOrder = (orders: IOrder[]): IOrder | undefined => {
  const isSubmitted = (order: IOrder) => (order.orderStatus > orderStatusCode.created ? 1 : 0);
  return [...orders].sort((a, b) => isSubmitted(b) - isSubmitted(a) || String(b._id).localeCompare(String(a._id)))[0];
};

const selectProjectInfo = createSelector([(state) => state.auth.user, (_, project) => project], (user, project) => {
  const orders: IOrder[] | null = project.orders ? [...project.orders] : null;
  if (!orders && !project.orderStatus && project.orderStatus !== 0) {
    // No orders to read, so the card sits at the start of its progress bar. Every other field is
    // undefined - including cannotDelete, which the card reads as permission to delete. That
    // is wrong and it is not this ticket's to change.
    return { activeStep: 0 };
  }

  // Which of a project's orders speaks for it: the newest submitted one, falling back to the
  // newest draft when nothing was ever submitted. This mirrors exactly what the server does when
  // it builds the complete set - aw-server project_search.js adds isSubmitted, sorts it and _id
  // descending, and keeps one. Change either and you must change the other, or the two surfaces
  // describe the same project differently. ObjectIds are generated in time order, so recency
  // sorts by _id, client-side as server-side.
  //
  // When the project arrives already flattened it IS its representative order - the server made
  // this same choice before sending it.
  const representativeOrder = orders
    ? pickRepresentativeOrder(orders)
    : project;

  // Whether anything was ever delivered, and whether anything was ever placed. Read across every
  // order where they are available. On the flattened shape, delivered comes off the server's
  // hasDelivered: the representative is the newest submitted order, so a project delivered once
  // and ordered again would otherwise lose its delivery (and its route to the deliverables).
  // Placed is safe to read off the representative: it is submitted whenever any order is.
  const orderFinalized = orders
    ? orders.some((o) => o.orderStatus === orderStatusCode.done)
    : (project.hasDelivered ?? representativeOrder?.orderStatus === orderStatusCode.done);
  const projectProcessing = orders
    ? orders.some((order) => order.orderStatus > orderStatusCode.created)
    : (representativeOrder?.orderStatus || 0) > orderStatusCode.created;

  // Always false: cadFiles is not a field on an order - they are their own collection, joined by
  // ownerOrder, and no projects endpoint attaches them. Left reading the same way rather than
  // hardcoded, so the day cad files are attached this starts working instead of staying dead.
  const expressAIReady = orders?.some((order) => order.cadFiles?.some((cadfile) => (cadfile.expressAIUpload && cadfile.expressAIEmailSent)));

  // Check if project is created by the current user
  const isProjectCreatedByUser = project.owner === user._id;
  // Variable to check if a project can be deleted
  const cannotDelete = projectProcessing || !isProjectCreatedByUser;
  // Undefined only when the project has no orders at all. A draft-only project reports 0;
  // every reader treats 0 and undefined alike - both falsy, both rendering as not submitted.
  const orderStatus = representativeOrder?.orderStatus;
  // Whether the project holds an estimate anywhere - not whether the order representing it is
  // one. Read across every order where they are available, and off the server's hasEstimate
  // where they are not, exactly as orderFinalized and projectProcessing above. The map chip asks
  // the narrower question and reads isEstimate; both fields travel so neither surface has to
  // adopt the other's reading.
  const orderEstimate = orders
    ? orders.some((order) => order.isEstimate)
    : Boolean(project.hasEstimate);
  // The newest order's estimate wherever one exists, whatever that order's status - what the
  // paginated list gave the card by scanning every order. Read across the orders where they are
  // available, and off the server's latestDeliveryEstimate where they are not; deliveryEstimate
  // on the flattened shape is the representative's, which is the rail's reading.
  const orderEstimatedDeliveryDate = orders
    ? ([...orders]
      .filter((order) => order.deliveryEstimate)
      .sort((a, b) => String(b._id).localeCompare(String(a._id)))[0]?.deliveryEstimate || null)
    : project.latestDeliveryEstimate || null;
  // The due date the cards and rows print: the representative order's deliveryDate, which the
  // flattened shape carries under the same name.
  const deliveryDate = representativeOrder?.deliveryDate || null;
  const { imageryRequest, rasterRequest } = project;
  // The same reading the map rail and the pin card take; they render its label, the grid card
  // renders how far along its step is.
  const { step: activeStep } = resolveProjectStatus({ orderStatus, isEstimate: orderEstimate });
  // Narrower than the shared reading's 'inProgress', which also covers drafting and QC: this
  // gates the card's active-work overlay, and a project in drafting goes back to offering
  // "View Project".
  const isInProgress = orderStatus > orderStatusCode.created && orderStatus < orderStatusCode.manualDrafting;
  return {
    activeStep,
    isInProgress,
    cannotDelete,
    orderFinalized,
    expressAIReady,
    orderEstimate,
    orderStatus,
    imageryRequest,
    rasterRequest,
    orderEstimatedDeliveryDate,
    deliveryDate,
  };
});

const selectMapCoordinates = (state: RootState) => state.projectList.mapCoordinates;

// The one filter the server does not apply: "Location Not Defined" keeps, out of the set the
// server answered for every project, those with no middlePoint. The dashboard's grid and every
// map-view surface read the set through here, so the filter means the same thing on each.
const selectFilteredCoordinates = createSelector(
  [selectMapCoordinates, selectEffectiveOrderSortFilter],
  (projects, filter) => (filter === 'noLocation'
    ? projects.filter((project: IProjectMapCoordinate) => !hasLocation(project))
    : projects),
);

// The complete set narrowed to the folder the map view is scoped to, or the whole set (AUT-6432).
// Every map-view surface - the rail's cards, the map's pins, the unlocated-projects chip - reads
// this, so scoping a folder can never show a card the map does not plot or the other way round.
const selectScopedCoordinates = createSelector(
  [selectFilteredCoordinates, selectMapFolderId],
  (projects, mapFolderId) => (mapFolderId
    ? projects.filter((project: IProjectMapCoordinate) => project.folderId === mapFolderId)
    : projects),
);

// Derived from the set rather than read from the server's count, so it follows the scope; the
// set lists every matching project, located or not, so the two agree unscoped.
const selectScopedMissingLocationCount = createSelector(
  [selectScopedCoordinates],
  (projects) => projects.filter((project: IProjectMapCoordinate) => !hasLocation(project)).length,
);

// The dedicated coordinates fetch covers every project matching the current search/filter.
// Deliberately NO grid-list fallback: the pin card lookup, the rail, the summary panel, and
// the empty state all read mapCoordinates, so fallback pins would be unclickable dead ends
// under UI claiming nothing exists.
const selectMarkerSourceProjects = selectScopedCoordinates;

// Deliberately independent of the hovered project id: this array becomes the map's GeoJSON
// source, and rebuilding it re-uploads and re-clusters the whole source.
const selectAllCoordinates = createSelector(
  [selectMarkerSourceProjects],
  (projects) => {
    const coordinatesInfo = projects
      .filter((project: IProjectMapCoordinate) => hasLocation(project))
      .map((p: IProjectMapCoordinate) => ({
        _id: p._id,
        coords: [p.middlePoint!.longitude, p.middlePoint!.latitude],
      }));
    return coordinatesInfo;
  },
);

// The full coordinate entry for the pin whose overview card is open - undefined once a search,
// filter or scope change stops plotting that project, which closes the card. Read off the
// scoped set for that reason: the raw set still holds a project the current filter dropped.
const selectSelectedPinProject = createSelector(
  [
    selectScopedCoordinates,
    (state: RootState) => state.projectList.selectedPinProjectId,
  ],
  (projects: IProjectMapCoordinate[], selectedPinProjectId: string | null) => (selectedPinProjectId
    ? projects.find((coordinate) => coordinate._id === selectedPinProjectId)
    : undefined),
);

export {
  selectProjectInfo, selectAllCoordinates, selectEffectiveOrderSortFilter, selectSelectedPinProject,
  selectFilteredCoordinates, selectScopedCoordinates, selectScopedMissingLocationCount,
};
